/**
 * AIVaryModal - Modal for AI-generated element variations
 *
 * Calls the generateVariation API and lets the user apply the suggested change
 * to the element in the iframe via bridge messages.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useGenerateVariation } from "@/hooks/useExperiments";
import {
  logExperimentDegradation,
  logExperimentEvent,
  reportExperimentFailure,
} from "@/lib/observability/experiments";
import type { ParentBridge, ElementEditData } from "@humblytics/editor-core";
import { toast } from "sonner";
import { HugeiconsIcon } from "@hugeicons/react"
import { Loading01Icon, SparklesIcon, Tick01Icon } from "@hugeicons/core-free-icons"

interface AIVaryModalProps {
  elementData: ElementEditData;
  bridge: ParentBridge;
  onClose: () => void;
}

export function AIVaryModal({
  elementData,
  bridge,
  onClose,
}: AIVaryModalProps) {
  const [applied, setApplied] = useState(false);
  const generateVariation = useGenerateVariation();

  const handleGenerate = async () => {
    try {
      const result = await generateVariation.mutateAsync({
        elementData: {
          selector: elementData.selector,
          html: `<${elementData.tagName}>${elementData.textContent}</${elementData.tagName}>`,
          text: elementData.textContent,
          styles: elementData.computedStyles,
          context: `Element: ${elementData.tagName}`,
        },
        variationType: "text",
        prompt: "Generate 3 compelling alternative variations for this element. Focus on conversion optimization.",
      });

      const suggestion = result.data;
      if (!suggestion?.changes) {
        logExperimentDegradation("ai_vary_unusable_response", {}, {
          reason: "no_changes_returned",
        });
        toast.error("No variations generated", {
          description: "Please try again with different content.",
        });
        return;
      }

      // Apply the first suggestion - API may return array or single object
      const changes = Array.isArray(suggestion.changes)
        ? suggestion.changes[0]
        : suggestion.changes;
      const newText =
        typeof changes === "string"
          ? changes
          : (changes as { newText?: string; text?: string })?.newText ||
            (changes as { newText?: string; text?: string })?.text;

      if (newText) {
        bridge.send({
          type: "APPLY_TEXT_CHANGE",
          payload: { selector: elementData.selector, text: newText },
        });
        setApplied(true);
        logExperimentEvent("variation_applied", {}, { experimentType: "text" });
        toast.success("Variation applied", {
          description: suggestion.reasoning?.slice(0, 80) + "...",
        });
        onClose();
      } else if (typeof changes === "object" && changes !== null) {
        // Try style changes
        const styles = changes as Record<string, string>;
        if (Object.keys(styles).length > 0) {
          bridge.send({
            type: "APPLY_STYLE_CHANGES",
            payload: { selector: elementData.selector, styles },
          });
          setApplied(true);
          logExperimentEvent("variation_applied", {}, { experimentType: "style" });
          toast.success("Style variation applied");
          onClose();
        } else {
          logExperimentDegradation("ai_vary_unusable_response", {}, {
            reason: "unexpected_response_format",
          });
          toast.error("Could not apply variation", {
            description: "Unexpected response format.",
          });
        }
      } else {
        logExperimentDegradation("ai_vary_unusable_response", {}, {
          reason: "no_applicable_changes",
        });
        toast.error("Could not apply variation", {
          description: "No applicable changes in the response.",
        });
      }
    } catch (error: unknown) {
      reportExperimentFailure("ai_vary_generate", error, {});
      toast.error("Failed to generate variation", {
        description: (error as Error)?.message || "Please try again.",
      });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HugeiconsIcon icon={SparklesIcon} size={20} className="text-primary" />
            AI Optimize
          </DialogTitle>
          <DialogDescription>
            Generate AI-powered optimizations for this element. The suggestion
            will be applied when you click Generate & Apply.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <p className="text-muted-foreground text-xs mb-1">
              Current content
            </p>
            <p className="text-foreground line-clamp-2 font-mono text-xs">
              {elementData.textContent || "(empty)"}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generateVariation.isPending}
            className="gap-2"
          >
            {generateVariation.isPending ? (
              <>
                <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
                Generating...
              </>
            ) : applied ? (
              <>
                <HugeiconsIcon icon={Tick01Icon} size={16} />
                Applied
              </>
            ) : (
              <>
                <HugeiconsIcon icon={SparklesIcon} size={16} />
                Generate & Apply
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
